import NiceModal from "@ebay/nice-modal-react";
import { bffRequester, requester } from "App";
import { mobileNoFormat } from "InitialData/regExp";
import Dummy from "components/Dummy";
import P from "components/P";
import BackButton from "components/buttons/BackButton";
import CustomButton from "components/buttons/CustomButton";
import InputText from "components/inputs/signUp/InputText";
import FlexChild from "layouts/flex/FlexChild";
import HorizontalFlex from "layouts/flex/HorizontalFlex";
import VerticalFlex from "layouts/flex/VerticalFlex";
import Center from "layouts/wrapper/Center";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import puffuLogo from "resources/img/puffu/puffuLogo.svg";
import { devLog } from "utils/logger";
import puffuIcon from "resources/img/puffuIcon.svg";

function IdentityVerification(props) {
    const { id } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // 웹뷰 체크 함수
    const isOurAppWebView = () => {
        const userAgent = navigator.userAgent.toLowerCase();

        // 웹뷰 감지 로직
        const isWebView = (() => {
            const isIOSWebView = /(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(userAgent);
            const isAndroidWebView = /wv/.test(userAgent) || /android.*samsungbrowser\/(?!.*chrome)/i.test(userAgent);

            return isIOSWebView || isAndroidWebView;
        })();

        // 웹뷰 확인
        if (isWebView) {
            if (window.BRIDGE && window.BRIDGE.isOurAppWebView && window.BRIDGE.isOurAppWebView()) {
                return true;
            } else if (window.webkit?.messageHandlers?.BRIDGE) {
                return true;
            }
        }

        return false;
    };

    // 디버깅: 초기 props 로깅
    devLog("[IdentityVerification] Initial props:", props);

    // props를 state로 관리하여 뒤로가기에도 유지되도록 함
    const [propsState, setPropsState] = useState({
        authType: props.authType,
        verificationData: props.verificationData || {},
        storeCode: props.verificationData?.storeCode || props.storeCode,
        deviceCode: props.verificationData?.deviceCode || props.deviceCode,
        paymentData: props.verificationData?.paymentData || props.paymentData,
        mobileNumber: props.verificationData?.mobileNumber || props.mobileNumber,
        // 기존 props 하위 호환성 유지
        door: props.door,
        payment: props.payment,
        puffuOrder: props.puffuOrder
    });

    // props가 변경되면 state 업데이트 (필요한 경우)
    useEffect(() => {
        devLog("[IdentityVerification] props 변경 감지:", props);
        if (props.authType !== propsState.authType ||
            props.verificationData !== propsState.verificationData ||
            props.storeCode !== propsState.storeCode ||
            props.deviceCode !== propsState.deviceCode) {

            setPropsState({
                authType: props.authType,
                verificationData: props.verificationData || {},
                storeCode: props.verificationData?.storeCode || props.storeCode,
                deviceCode: props.verificationData?.deviceCode || props.deviceCode,
                paymentData: props.verificationData?.paymentData || props.paymentData,
                mobileNumber: props.verificationData?.mobileNumber || props.mobileNumber,
                door: props.door,
                payment: props.payment,
                puffuOrder: props.puffuOrder
            });

            devLog("[IdentityVerification] propsState 업데이트됨");
        }
    }, [props]);

    // 디버깅: 추출된 props 로깅
    devLog("[IdentityVerification] propsState:", propsState);

    // 프롭스 State에서 필요한 데이터 직접 참조
    const { authType, verificationData, storeCode, deviceCode, paymentData, mobileNumber } = propsState;

    // 인증 유형 확인 함수 (코드 중복 제거) - propsState 사용
    const isAuthType = (type) => {
        const result = propsState.authType === type ||
            (propsState[type] === true) || // 하위 호환성 유지
            (propsState.verificationData && propsState.verificationData.type === type);

        // 디버깅: 타입 체크 로깅
        devLog(`[IdentityVerification] isAuthType(${type}): ${result}`, {
            "propsState.authType === type": propsState.authType === type,
            "propsState[type] === true": propsState[type] === true,
            "propsState.verificationData?.type === type": propsState.verificationData?.type === type
        });

        return result;
    };

    // 모든 인증 관련 정보를 state로 통합 관리
    const [authState, setAuthState] = useState({
        step: 1,
        authMethod: null,
        networkOperator: null,
        phoneNumber: mobileNumber || '',
        name: '',
        residentNumber: '',
        residentNumberBack: '',
        gender: '',
        nation: '',
    });

    useEffect(() => {
        if (mobileNumber) {
            setAuthState(prev => ({
                ...prev,
                phoneNumber: mobileNumber
            }));
        }
    }, [mobileNumber]);

    useEffect(() => {
        // 초기 상태를 history state에 저장
        const initialState = {
            ...authState,
            propsState, // propsState도 함께 저장
            fromVerification: true
        };

        window.history.replaceState(initialState, '', window.location.pathname);

        // 브라우저 뒤로가기 이벤트 처리
        const handlePopState = (event) => {
            devLog("[IdentityVerification] popstate 이벤트 발생, event.state:", event.state);

            if (!event.state?.fromVerification) {
                devLog("[IdentityVerification] 검증 상태 없음, 뒤로가기");
                window.history.back();
                return;
            }

            devLog("[IdentityVerification] 상태 복원 시도", event.state);
            if (event.state) {
                // authState 복원
                setAuthState(prev => ({
                    ...prev,
                    ...event.state
                }));

                // propsState 복원 (저장되어 있다면)
                if (event.state.propsState) {
                    devLog("[IdentityVerification] propsState 복원:", event.state.propsState);
                    setPropsState(event.state.propsState);
                }
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    // 디버깅: 마운트 시 인증 타입 체크
    useEffect(() => {
        devLog("[IdentityVerification] 컴포넌트 마운트 - 인증 타입 체크:", {
            isPuffuOrder: isAuthType("puffuOrder"),
            isPayment: isAuthType("payment"),
            isDoor: isAuthType("door")
        });

        devLog("[IdentityVerification] 컴포넌트 마운트 - 데이터:", {
            "location.state/verificationData": verificationData,
            "authType": authType,
            "props": props
        });
    }, []);

    // 단계 변경 시 히스토리 상태 관리
    const handleStepChange = (newStep) => {
        const newState = {
            ...authState,
            propsState, // propsState도 함께 저장
            step: newStep,
            fromVerification: true
        };

        window.history.pushState(newState, '', window.location.pathname);
        setAuthState(prev => ({
            ...prev,
            step: newStep
        }));

        devLog(`[IdentityVerification] 단계 변경: ${authState.step} → ${newStep}, 상태 저장:`, newState);
    };

    // 인증 방식 선택 처리
    const handleAuthMethodSelect = (method) => {
        setAuthState(prev => ({
            ...prev,
            authMethod: method
        }));
    };

    // 통신사 선택 처리
    const handleOperatorSelect = (operator) => {
        setAuthState(prev => ({
            ...prev,
            networkOperator: operator
        }));
    };

    // 주민번호 처리
    const handleResidentNumberChange = (value) => {
        if (value.length <= 6) {
            setAuthState(prev => ({
                ...prev,
                residentNumber: value
            }));
        }
    };

    const handleResidentNumberBackChange = (value) => {
        if (value.length <= 1) {
            const gender = value ? (parseInt(value) % 2 === 1 ? "1" : "2") : '';
            setAuthState(prev => ({
                ...prev,
                residentNumberBack: value,
                gender,
                nation: value ? "0" : ''
            }));
        }
    };

    // 주민번호 앞자리를 8자리 생년월일로 변환하는 함수
    const getBirthDateFromResidentNumber = (residentNumber, residentNumberBack) => {
        if (residentNumber.length === 6 && residentNumberBack) {
            const prefix = parseInt(residentNumberBack) < 3 ? "19" : "20";
            return prefix + residentNumber;
        }
        return "";
    };

    // 전화번호 입력 핸들러 추가
    const handlePhoneNumberChange = (value) => {
        setAuthState(prev => ({
            ...prev,
            phoneNumber: value
        }));
    };

    const onAuthTypeClick = () => {
        // 전화번호 유효성 검사 추가
        if (!authState.phoneNumber) {
            toast.error("전화번호를 입력해주세요", {
                autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
            });
            return;
        }

        if (authState.authMethod === "PASS") {
            if (authState.networkOperator) {


                passOnClick();
            } else {
                toast.error("통신사를 선택해주세요", {
                    autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                });
            }
        } else if (authState.authMethod === "SMS") {
            if (authState.networkOperator) {


                smsRequestClick();
            } else {
                toast.error("통신사를 선택해주세요", {
                    autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                });
            }
        }
    };

    // passOnClick 함수에서 NiceModal.show 호출 부분 수정
    const showVerificationModal = (result, tokenData, type, data, resultCode) => {
        devLog("[IdentityVerification] showVerificationModal 호출 - 인증 타입:", type);

        // 인증 타입 판단 로직 및 결과 로깅 (각 타입별 직접 확인)
        const isPayment = isAuthType("payment");
        const isPuffuOrder = isAuthType("puffuOrder");
        const isDoor = isAuthType("door");
        const isCardAuth = isAuthType("card_payment_auth");

        devLog("[IdentityVerification] showVerificationModal - 인증 타입 확인 결과:", {
            isPayment,
            isPuffuOrder,
            isDoor,
            authType,
            verificationDataType: verificationData?.type,
            propsPayment: props.payment,
            propsPuffuOrder: props.puffuOrder,
            propsDoor: props.door
        });

        // 로그인용 파라미터 로깅
        devLog("[IdentityVerification] showVerificationModal - 모달에 전달할 파라미터:", {
            storeCode,
            deviceCode,
            paymentData,
            type
        });

        let logData = {
            "storeCode": storeCode,
            "resultCode": resultCode,
            "authType": type
        }

        if (isPayment) {
            devLog("[IdentityVerification] payment 인증 모달 표시");

            const updatedLogData = {
                ...logData,
                purpose: "payment"  // 조건이 이미 isDoor에서 걸렸으므로 바로 넣어도 OK
            };

            requester.createAdultVerificationLog(updatedLogData, (res) => {
                if (res.code === 0) {
                    NiceModal.show("numberVerificationByPayment", {
                        data: result,
                        token: tokenData,
                        type,
                        info: data,
                        storeCode,
                        paymentData
                    });
                }
            });

        } else if (isPuffuOrder) {
            devLog("[IdentityVerification] puffuOrder 인증 모달 표시");
            const updatedLogData = {
                ...logData,
                purpose: "puffuOrder"  // 조건이 이미 isDoor에서 걸렸으므로 바로 넣어도 OK
            };

            requester.createAdultVerificationLog(updatedLogData, (res) => {
                if (res.code === 0) {
                    NiceModal.show("numberVerificationByPuffuOrder", {
                        data: result,
                        token: tokenData,
                        type,
                        info: data,
                        storeCode,
                        deviceCode
                    });
                }
            });


        } else if (isDoor) {
            devLog("[IdentityVerification] door 인증 모달 표시");

            const updatedLogData = {
                ...logData,
                purpose: "door"  // 조건이 이미 isDoor에서 걸렸으므로 바로 넣어도 OK
            };
            devLog(updatedLogData);

            // 디버깅: iotId 및 deviceCode 확인 로그 추가
            devLog("[IdentityVerification] door 인증 관련 데이터:", {
                verificationData,
                iotId: verificationData?.iotId,
                deviceCode,
                storeCode,
                purpose: "door"
            });
            requester.createAdultVerificationLog(updatedLogData, (res) => {
                if (res.code === 0) {
                    NiceModal.show("numberDoorVerification", {
                        data: result,
                        token: tokenData,
                        type,
                        info: data,
                        storeCode,
                        deviceCode,
                        // iotId도 함께 전달 (deviceCode와 동일하더라도 명시적으로 전달)
                        iotId: verificationData?.iotId || deviceCode
                    });
                }
            });

        } else if (isCardAuth) {
            devLog("[IdentityVerification] door 인증 모달 표시");

            const updatedLogData = {
                ...logData,
                purpose: "card_payment_auth"  // 조건이 이미 isDoor에서 걸렸으므로 바로 넣어도 OK
            };
            devLog(updatedLogData);

            // 디버깅: iotId 및 deviceCode 확인 로그 추가
            devLog("[IdentityVerification] door 인증 관련 데이터:", {
                verificationData,
                iotId: verificationData?.iotId,
                deviceCode,
                storeCode,
                purpose: "card_payment_auth"
            });
            requester.createAdultVerificationLog(updatedLogData, (res) => {
                if (res.code === 0) {
                    NiceModal.show("numberVerificationByCardAuthModal", {
                        data: result,
                        token: tokenData,
                        type,
                        info: data,
                        storeCode,
                        deviceCode,
                        // iotId도 함께 전달 (deviceCode와 동일하더라도 명시적으로 전달)
                        iotId: verificationData?.iotId || deviceCode
                    });
                }
            });

        } else {
            devLog("[IdentityVerification] 일반 인증 모달 표시");

            const updatedLogData = {
                ...logData,
                purpose: "login"  // 조건이 이미 isDoor에서 걸렸으므로 바로 넣어도 OK
            };

            requester.createAdultVerificationLog(updatedLogData, (res) => {
                if (res.code === 0) {
                    NiceModal.show("numberVerification", {
                        data: result,
                        token: tokenData,
                        type,
                        info: data,
                        storeCode
                    });
                }
            });
        }
    };

    // passOnClick 함수 수정
    const passOnClick = () => {
        bffRequester.getToken((result) => {
            let tokenData = result;
            let token = {
                "encryptMOKToken": tokenData.encryptMOKToken,
                "publicKey": tokenData.publicKey
            };
            let jsonData = JSON.stringify(token);

            let data = {
                "serviceType": "telcoAuth",
                "providerid": authState.networkOperator,
                "reqAuthType": "PASS",
                "usageCode": "01005",
                "userName": authState.name,
                "userPhoneNum": authState.phoneNumber,
                "retTransferType": "MOKResult",
                "normalFlag": "Y",
                "MOKAuthRequestData": jsonData
            };

            devLog(data);

            bffRequester.apiRequest(data, (result) => {
                const outerMOKConfirmData = result.MOKConfirmData;
                const innerMOKConfirmData = JSON.parse(outerMOKConfirmData);

                if (innerMOKConfirmData.resultCode === "2000") {
                    showVerificationModal(result, tokenData, "PASS", data, innerMOKConfirmData.resultCode);
                } else {
                    toast.error(innerMOKConfirmData.resultMsg, {
                        autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                    });
                }
            });
        });
    };

    // smsRequestClick 함수 수정
    const smsRequestClick = () => {
        bffRequester.getToken((result) => {
            let tokenData = result;
            let token = {
                "encryptMOKToken": tokenData.encryptMOKToken,
                "publicKey": tokenData.publicKey
            };
            let jsonData = JSON.stringify(token);

            let data = {
                "serviceType": "telcoAuth",
                "providerid": authState.networkOperator,
                "reqAuthType": "SMS",
                "usageCode": "01005",
                "userName": authState.name,
                "userPhoneNum": authState.phoneNumber,
                "userBirthday": getBirthDateFromResidentNumber(authState.residentNumber, authState.residentNumberBack),
                "userGender": authState.gender,
                "sendMsg": "본인확인 인증번호[000000]를 화면에 입력해주세요.",
                "replyNumber": "01048947486",
                "retTransferType": "MOKResult",
                "userNation": authState.nation,
                "normalFlag": "Y",
                "MOKAuthRequestData": jsonData
            };

            bffRequester.apiRequest(data, (result) => {
                const outerMOKConfirmData = result.MOKConfirmData;
                const innerMOKConfirmData = JSON.parse(outerMOKConfirmData);

                if (innerMOKConfirmData.resultCode === "2000") {
                    showVerificationModal(result, tokenData, "SMS", data, innerMOKConfirmData.resultCode);
                } else {
                    toast.error(innerMOKConfirmData.resultMsg, {
                        autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                    });
                }
            });
        });
    };

    // 백버튼 처리를 위한 상태 관리
    const handleGoBack = () => {
        devLog("[IdentityVerification] 뒤로가기 호출, 현재 단계:", authState.step);

        if (authState.step === 1) {
            // 첫 단계에서는 브라우저 히스토리 스택을 사용하여 이전 페이지로 이동
            devLog("[IdentityVerification] 첫 단계에서 뒤로가기, window.history.back() 호출");
            window.history.back();
        } else {
            // 그 외에는 단계 변경
            devLog(`[IdentityVerification] 단계 변경: ${authState.step} → ${authState.step - 1}`);
            handleStepChange(authState.step - 1);
        }
    };

    const renderStep = () => {
        switch (authState.step) {
            case 1:
                return (
                    <VerticalFlex gap={20}>
                        <FlexChild>
                            <Center width={"100%"} textAlign={"left"}>
                                <P color={"black"} weight={700} size={18}>1. 인증 방식을 선택하세요.</P>
                            </Center>
                        </FlexChild>
                        <FlexChild>
                            <div style={{ display: "flex", gap: "10px", width: "100%" }}>
                                <div
                                    style={{
                                        flex: 1,
                                        padding: "20px",
                                        borderRadius: "8px",
                                        backgroundColor: authState.authMethod === "PASS" ? "#FF5B14" : "#ffffff",
                                        border: "1px solid #E1E1E1",
                                        cursor: "pointer",
                                        textAlign: "center"
                                    }}
                                    onClick={() => handleAuthMethodSelect("PASS")}
                                >
                                    <P weight={700} color={authState.authMethod === "PASS" ? "#ffffff" : "#666666"}>PASS 앱</P>
                                </div>
                                <div
                                    style={{
                                        flex: 1,
                                        padding: "20px",
                                        borderRadius: "8px",
                                        backgroundColor: authState.authMethod === "SMS" ? "#FF5B14" : "#ffffff",
                                        border: "1px solid #E1E1E1",
                                        cursor: "pointer",
                                        textAlign: "center"
                                    }}
                                    onClick={() => handleAuthMethodSelect("SMS")}
                                >
                                    <P weight={700} color={authState.authMethod === "SMS" ? "#ffffff" : "#666666"}>SMS 문자</P>
                                </div>
                            </div>
                        </FlexChild>
                        <FlexChild style={{ marginTop: "20px" }}>
                            <CustomButton
                                text="다음"
                                style={{
                                    width: "100%",
                                    height: "56px",
                                    backgroundColor: authState.authMethod ? "#FF5B14" : "#E1E1E1",
                                    border: "none",
                                    borderRadius: "8px",
                                    color: authState.authMethod ? "#ffffff" : "#999999",
                                    fontSize: "18px",
                                    fontWeight: "700",
                                    cursor: authState.authMethod ? "pointer" : "default"
                                }}
                                onClick={() => {
                                    if (authState.authMethod) {
                                        handleStepChange(2);
                                    } else {
                                        toast.error("인증 방식을 선택해주세요", {
                                            autoClose: 1000,
                                            position: toast.POSITION.BOTTOM_CENTER
                                        });
                                    }
                                }}
                            />
                        </FlexChild>
                    </VerticalFlex>
                );
            case 2:
                return (
                    <VerticalFlex gap={20}>
                        <FlexChild>
                            <Center width={"100%"} textAlign={"left"}>
                                <P color={"black"} weight={700} size={18}>2. 통신사를 선택해주세요.</P>
                            </Center>
                        </FlexChild>
                        <FlexChild>
                            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                {[
                                    { code: "SKT", value: "SKT" },
                                    { code: "KT", value: "KT" },
                                    { code: "LGU", value: "LGU" },
                                    { code: "SKT알뜰", value: "SKTMVNO" },
                                    { code: "KT알뜰", value: "KTMVNO" },
                                    { code: "LGU알뜰", value: "LGUMVNO" },
                                ].map((operator, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            flex: "1 1 30%",
                                            padding: "15px",
                                            borderRadius: "8px",
                                            backgroundColor: authState.networkOperator === operator.value ? "#FF5B14" : "#ffffff",
                                            border: "1px solid #E1E1E1",
                                            cursor: "pointer",
                                            textAlign: "center"
                                        }}
                                        onClick={() => handleOperatorSelect(operator.value)}
                                    >
                                        <P weight={700} color={authState.networkOperator === operator.value ? "#ffffff" : "#666666"}>{operator.code}</P>
                                    </div>
                                ))}
                            </div>
                        </FlexChild>
                        <FlexChild style={{ marginTop: "20px" }}>
                            <CustomButton
                                text="다음"
                                style={{
                                    width: "100%",
                                    height: "56px",
                                    backgroundColor: authState.networkOperator ? "#FF5B14" : "#E1E1E1",
                                    border: "none",
                                    borderRadius: "8px",
                                    color: authState.networkOperator ? "#ffffff" : "#999999",
                                    fontSize: "18px",
                                    fontWeight: "700",
                                    cursor: authState.networkOperator ? "pointer" : "default"
                                }}
                                onClick={() => {
                                    if (authState.networkOperator) {
                                        handleStepChange(3);
                                    } else {
                                        toast.error("통신사를 선택해주세요", {
                                            autoClose: 1000,
                                            position: toast.POSITION.BOTTOM_CENTER
                                        });
                                    }
                                }}
                            />
                        </FlexChild>
                    </VerticalFlex>
                );
            case 3:
                return (
                    <VerticalFlex gap={20}>
                        <FlexChild>
                            <Center width={"100%"} textAlign={"left"}>
                                <P color={"black"} weight={700} size={18}>3. 정보를 입력해주세요.</P>
                            </Center>
                        </FlexChild>
                        <FlexChild>
                            <VerticalFlex gap={20}>
                                <FlexChild>
                                    <P color={"black"} weight={700}>전화번호</P>
                                    <InputText
                                        value={authState.phoneNumber}
                                        onChange={(value) => setAuthState(prev => ({
                                            ...prev,
                                            phoneNumber: value
                                        }))}
                                        placeHolder="전화번호를 입력해주세요"
                                        mobileText
                                        number
                                        style={{
                                            marginTop: "8px",
                                            backgroundColor: "#ffffff",
                                            border: "1px solid #E1E1E1",
                                            borderRadius: "8px",
                                            height: "48px"
                                        }}
                                    />
                                </FlexChild>
                                <FlexChild>
                                    <P color={"black"} weight={700}>이름</P>
                                    <InputText
                                        value={authState.name}
                                        onChange={value => setAuthState(prev => ({ ...prev, name: value }))}
                                        placeHolder="실명을 입력해주세요"
                                        mobileText
                                        style={{
                                            marginTop: "8px",
                                            backgroundColor: "#ffffff",
                                            border: "1px solid #E1E1E1",
                                            borderRadius: "8px",
                                            height: "48px"
                                        }}
                                    />
                                </FlexChild>
                                {authState.authMethod === "SMS" && (
                                    <FlexChild>
                                        <P color={"black"} weight={700}>주민등록번호</P>
                                        <div style={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems: "center",
                                            marginTop: "8px"
                                        }}>
                                            <InputText
                                                number
                                                value={authState.residentNumber}
                                                onChange={handleResidentNumberChange}
                                                placeHolder="앞 6자리"
                                                maxLength={6}
                                                style={{
                                                    flex: 1,
                                                    backgroundColor: "#ffffff",
                                                    border: "1px solid #E1E1E1",
                                                    borderRadius: "8px",
                                                    height: "48px"
                                                }}
                                            />
                                            <span style={{ color: "black" }}>-</span>
                                            <InputText
                                                number
                                                value={authState.residentNumberBack}
                                                onChange={handleResidentNumberBackChange}
                                                placeHolder="뒷 1자리"
                                                maxLength={1}
                                                style={{
                                                    width: "80px",
                                                    backgroundColor: "#ffffff",
                                                    border: "1px solid #E1E1E1",
                                                    borderRadius: "8px",
                                                    height: "48px"
                                                }}
                                            />
                                            <span style={{ color: "#666666" }}>●●●●●●</span>
                                        </div>
                                    </FlexChild>
                                )}
                                <FlexChild style={{ marginTop: "20px" }}>
                                    <CustomButton
                                        text="인증요청"
                                        style={{
                                            width: "100%",
                                            height: "56px",
                                            backgroundColor: "#FF5B14",
                                            border: "none",
                                            borderRadius: "8px",
                                            color: "#ffffff",
                                            fontSize: "18px",
                                            fontWeight: "700"
                                        }}
                                        onClick={onAuthTypeClick}
                                    />
                                </FlexChild>
                            </VerticalFlex>
                        </FlexChild>
                    </VerticalFlex>
                );
            default:
                return null;
        }
    };

    return (
        <VerticalFlex gap={20} backgroundColor={"#ffffff"}>
            <FlexChild width={"100%"}>
                <VerticalFlex>
                    <FlexChild padding={"10px 0"}>
                        <HorizontalFlex>
                            <FlexChild width={30}>
                                <BackButton
                                    color="black"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleGoBack();
                                    }}
                                />
                            </FlexChild>
                            <FlexChild>
                                <Center>
                                    <P weight={900} size={20} color={"black"}>본인인증</P>
                                </Center>
                                <Dummy width={"30px"} />
                            </FlexChild>
                        </HorizontalFlex>
                    </FlexChild>
                    <FlexChild width={"100%"} height={"1px"} backgroundColor={"#E1E1E1"} />
                </VerticalFlex>
            </FlexChild>
            {!isOurAppWebView() &&
                <FlexChild width={"90%"}>
                    <div
                        style={{
                            backgroundColor: '#FFF7F4',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer'
                        }}
                        onClick={() => navigate('/appDown')}
                    >
                        <HorizontalFlex>
                            <FlexChild width={"max-content"}>
                                <img src={puffuIcon} alt="app icon" style={{ width: '24px', height: '24px', marginRight: '12px' }} />
                            </FlexChild>
                            <FlexChild width={"max-content"}>
                                <P weight={700} size={14} color={"#212121"} style={{ lineHeight: '1.4' }}>
                                    푸푸 앱으로 더 빠르고 간편하게!
                                </P>
                            </FlexChild>
                            <FlexChild justifyContent={"flex-end"}>
                                <P size={14} color={"#FF5B14"} weight={700} style={{ marginLeft: 'auto' }}>
                                    설치 &gt;
                                </P>
                            </FlexChild>
                        </HorizontalFlex>
                    </div>
                </FlexChild>
            }
            <FlexChild width={"90%"}>
                {renderStep()}
            </FlexChild>
        </VerticalFlex>
    );
}

export default IdentityVerification;